import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { PageMeta, faqJsonLd, SITE_URL, OG_IMAGE } from "@/lib/seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
import { api, blogHeroUrl } from "@/lib/api";

function PostCard({ p }) {
  return (
    <li className="list-none">
      <Link to={`/blog/${p.slug}`} data-testid={`topic-post-${p.slug}`} className="group block border border-obsidian-600 bg-obsidian-800/30 hover:border-terracotta/70 transition-colors h-full">
        {p.hero_url && <img src={blogHeroUrl(p.id)} alt={p.hero_alt || p.title} loading="lazy" className="w-full aspect-[16/9] object-cover" />}
        <div className="p-5">
          <h3 className="font-serif text-xl text-bone-100 group-hover:text-terracotta transition-colors leading-snug mb-2">{p.title}</h3>
          <p className="font-sans text-sm text-bone-400 leading-relaxed">{p.excerpt}</p>
        </div>
      </Link>
    </li>
  );
}

export default function BlogTopic() {
  const { topic } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setData(null);
    api.get(`/blog/topics/${topic}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [topic]);

  if (missing) return <Navigate to="/blog" replace />;
  const t = data?.topic;

  return (
    <div className="min-h-screen bg-obsidian-900" data-testid="blog-topic-page">
      {t && <PageMeta title={`${t.title} · Umbra Journal`} description={t.description} path={`/blog/topic/${topic}`} jsonLd={[faqJsonLd(t.faq)]} image={t.cover_url ? `${SITE_URL}${t.cover_url}` : OG_IMAGE} />}
      <SiteHeader />
      <main className="max-w-6xl mx-auto px-6 md:px-12 pt-14 sm:pt-20 pb-24">
        <Link to="/blog" className="font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta" data-testid="topic-back-link">← All field notes</Link>
        {!t ? (
          <Loader2 className="mt-10 h-6 w-6 animate-spin text-bone-400" />
        ) : (
          <>
            {t.cover_url && (
              <img
                src={`${BACKEND}${t.cover_url}&w=1200`}
                srcSet={`${BACKEND}${t.cover_url}&w=600 600w, ${BACKEND}${t.cover_url}&w=1200 1200w`}
                sizes="(max-width: 640px) 100vw, 1152px"
                width="1200"
                height="514"
                fetchpriority="high"
                alt={`${t.name} — Umbra Journal topic cover`}
                className="mt-8 w-full aspect-[21/9] object-cover border border-obsidian-600" data-testid="topic-cover" />
            )}
            <p className="mt-8 font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-4">Topic · {t.name}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-bone-100 leading-tight max-w-4xl mb-8" data-testid="topic-title">{t.title}</h1>
            <p className="font-serif text-lg sm:text-xl text-bone-300 max-w-3xl leading-[1.75] mb-14" data-testid="topic-intro">{t.intro}</p>
            {data.posts.length ? (
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 p-0" data-testid="topic-posts">
                {data.posts.map((p) => <PostCard key={p.id} p={p} />)}
              </ul>
            ) : (
              <p className="font-serif italic text-bone-500" data-testid="topic-empty">Essays on this topic are on their way.</p>
            )}
            <section className="mt-20 max-w-3xl border-t border-obsidian-600 pt-12" data-testid="topic-faq">
              <h2 className="font-serif text-2xl md:text-3xl text-bone-100 mb-8">Common questions</h2>
              <dl className="space-y-7">
                {t.faq.map((f, i) => (
                  <div key={i}>
                    <dt className="font-serif text-xl text-bone-100 mb-2">{f.q}</dt>
                    <dd className="font-sans text-sm sm:text-base text-bone-300 leading-relaxed">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
